import { Button } from '@driftt/tide-core-legacy'
import cx from 'classnames'
import TeamAvailabilityPicker from '../TeamAvailabilityPicker'
import { normalizedTimezone } from 'utils/timezoneHelpers'
import moment from 'moment'

import { HOURS_AVAILABILITY_MODES, HOURS_EDIT_MODES, HOURS_MODE_COPY } from 'app/modules/teams/constants'

import './style.styl'

const CustomAvailabilitySettings = ({ settings, pending }) => {
  return (
    <div className={cx('team-hours-custom-hours-summary', { pending })}>
      {settings.openHours.map(hours => (
        <div key={JSON.stringify(hours)} className="team-hours-slot">
          <span className="team-hours-day-of-week">{hours.dayOfWeek.toLowerCase()}</span>
          &nbsp;from {moment(hours.opens, "hh:mm:ss").format("h:mmA")} to {moment(hours.closes, "hh:mm:ss").format("h:mmA")}
        </div>
      ))}
    </div>
  )
}

const RadioInput = ({ id, handleClick, isChecked, children, value, label, description }) => (
  <div key={id} className="team-hours-edit-option">
    <input
      type="radio"
      name="team-hours-setting"
      id={id}
      value={value}
      checked={isChecked}
      onChange={handleClick}
    />
    <label htmlFor={id}>
      <span className="team-hours-setting-title">{label}</span>
      {description && <span className="team-hours-setting-description">: {description}</span>}
    </label>
    {children}
  </div>
)

const TeamHoursManager = ({
  team,
  createNewAvailability,
  availabilitySettings,
  editMode,
  selectedHoursMode,
  handleEditHoursClick,
  setAvailabilityAlwaysOnlineMode,
  setAvailabilityScheduledHoursMode,
  setAvailabilityAlwaysOfflineMode,
  disableSave,
  isSaving,
  errorMessages,
  handleSaveClick,
  handleCancelClick,
  handleHoursSettingsChange,
  teamAvailabilities,
  updatePending,
}) => {
  const hoursModeContent = HOURS_MODE_COPY[selectedHoursMode]
  const savedAvailability = teamAvailabilities.find(ta => ta.id === team.teamAvailabilityId)
  return (
    <div className="team-hours-settings-container">
      <div className="team-hours-title">Team hours</div>
      <div className="team-hours-description">
        <span>Set online hours for this team so you can use targeting conditions to display playbooks or the widget
        only when this team is online.</span>
        <a href="https://help.drift.com/article/team-playbook-targeting" target="_blank"> Learn More</a>
      </div>
      {editMode === HOURS_EDIT_MODES.REVIEW && (
        <div className="team-hours-review-container flex-space-between">
        {selectedHoursMode === HOURS_AVAILABILITY_MODES.ALWAYS_OFFLINE && (
          <div className="team-hours-review-status-container">
            <div className="team-hours-review-status">
              <div className="team-hours-mode">This team was taken offline.</div>
            </div>
          </div>
        )}
        {selectedHoursMode === HOURS_AVAILABILITY_MODES.CUSTOM_HOURS &&
          !!savedAvailability && (
            <div className="team-hours-review-status-container flex-column">
              <div className="flex">
                <div className="team-hours-review-status">
                  <div className="team-hours-mode">Timezone</div>
                  <div className={cx('team-hours-custom-hours-summary', { updatePending })}>{normalizedTimezone(savedAvailability.timezone)}</div>
                </div>
              </div>
              <div className="flex">
                <div className="team-hours-review-status">
                  <div className="team-hours-mode">Scheduled Online Hours</div>
                  <CustomAvailabilitySettings pending={updatePending} settings={savedAvailability} />
                </div>
              </div>
            </div>
        )}
        {selectedHoursMode === HOURS_AVAILABILITY_MODES.ALWAYS_ONLINE && (
          <div className="team-hours-review-status-container">
            <div className="team-hours-review-status">
              <div className="team-hours-mode">{hoursModeContent.title}</div>
              <div className="team-hours-mode-description">{hoursModeContent.description}</div>
            </div>
          </div>
        )}
          <Button type="secondary" onClick={handleEditHoursClick} disabled={updatePending} className="team-members-edit-button">
            Edit hours
          </Button>
        </div>
      )}

      {editMode === HOURS_EDIT_MODES.EDIT && (
        <div className="team-hours-edit-container">
          <RadioInput
            id="alwaysOnlineHours"
            value={HOURS_AVAILABILITY_MODES.ALWAYS_ONLINE}
            isChecked={selectedHoursMode === HOURS_AVAILABILITY_MODES.ALWAYS_ONLINE}
            handleClick={setAvailabilityAlwaysOnlineMode}
            label={HOURS_MODE_COPY[HOURS_AVAILABILITY_MODES.ALWAYS_ONLINE].title}
            description={HOURS_MODE_COPY[HOURS_AVAILABILITY_MODES.ALWAYS_ONLINE].description}
          />
          <RadioInput
            id="scheduledHours"
            value={HOURS_AVAILABILITY_MODES.CUSTOM_HOURS}
            isChecked={selectedHoursMode === HOURS_AVAILABILITY_MODES.CUSTOM_HOURS}
            handleClick={setAvailabilityScheduledHoursMode}
            label={HOURS_MODE_COPY[HOURS_AVAILABILITY_MODES.CUSTOM_HOURS].title}
            description={HOURS_MODE_COPY[HOURS_AVAILABILITY_MODES.CUSTOM_HOURS].description}
          >
            {selectedHoursMode === HOURS_AVAILABILITY_MODES.CUSTOM_HOURS && (
              <div className="team-hours-picker">
                <TeamAvailabilityPicker
                  team={team}
                  createNew={createNewAvailability}
                  settings={availabilitySettings}
                  onChange={handleHoursSettingsChange}
                />
              </div>
            )}
          </RadioInput>
          <hr />
          <RadioInput
            id="alwaysOfflineHours"
            value={HOURS_AVAILABILITY_MODES.ALWAYS_OFFLINE}
            isChecked={selectedHoursMode === HOURS_AVAILABILITY_MODES.ALWAYS_OFFLINE}
            handleClick={setAvailabilityAlwaysOfflineMode}
            label="Take this team offline"
          />
          <div className="team-hours-save-controls">
            {!!errorMessages.length && <div className="error">{errorMessages.map(msg => msg)}</div>}
            <div>
              <Button type="primary" onClick={handleSaveClick} disabled={disableSave || isSaving} className="team-save-button">
                {isSaving ? `Saving...` : `Save`}
              </Button>{' '}
              <Button type="tertiary" onClick={handleCancelClick} className="team-cancel-button">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TeamHoursManager



// WEBPACK FOOTER //
// ./src/scripts/components/settings/Teams/TeamHoursManager/component.js